import { createFileRoute } from '@tanstack/react-router';
import { Schema } from 'effect';
import { PostPurchaseCarouselContent } from '@/components/astro/features/post-purchase-setup/carousel-content.island';
import { PostPurchaseNavHeader } from '@/components/astro/features/post-purchase-setup/nav-header.island';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import * as m from '@/paraglide/messages';
import {
  confirmPurchaseReturn,
  purchaseReturnDeps,
  purchaseReturnScripts,
  purchaseReturnSearchFields,
} from '@/lib/checkout/purchase-return';
import { seo } from '@/seo/seo';
import { usePurchaseCompletedEvent } from '@/hooks/use-purchase-completed-event';
import { useCheckoutReturnCleanup } from '@/services/subscription-service';

// Stripe `confirmPayment()` / `confirmSetup()` redirect the browser to this
// route with `?payment_intent=…&redirect_status=…` after the payment action.
//
// **This is the destination for EVERY checkout that confirms with Stripe, not
// just 3DS and wallets.** `check-out-form.tsx` passes no `redirect` option, so
// Stripe uses its default `redirect: 'always'`, which navigates the whole
// browser to `return_url` for every payment method including plain cards.
// (Opting out is `redirect: 'if_required'`, which this repo has never used.)
// The `localizedHomePath` returned after `confirmPayment` there is therefore
// effectively unreachable on success — the page is already gone.
//
// It is worth stating plainly, because the opposite reading implies
// `/post-purchase` pageviews undercount conversions, which would make every
// funnel and experiment built on that signal look broken. The Phase 0 audit
// measured the opposite (`docs/projects/complete/accounts-and-billing/audit-cookie-trackers.md`:
// `/post-purchase` pageview count matched the subscription count 1:1).
//
// So inbound traffic here is: every purchase that reached Stripe confirmation,
// failed/declined redirect-flow attempts (hence the `redirect_status` gate on
// the pixels), and the e2e test bypass. The one checkout outcome that does not
// arrive is `nothing-to-confirm` — an uncancel, or a subscription that
// activated on a card already on file — which never calls Stripe and goes
// straight to `/app/home`.
const PostPurchaseSearchSchema = Schema.toStandardSchemaV1(
  Schema.Struct(purchaseReturnSearchFields),
);

// This route lives at the routes root (NOT under _marketing) so the
// `_marketing.tsx` head() — which injects landing-stage trackers like
// TikTok ViewContent and Quora ViewContent — does not merge into this
// page and double-count them.
//
// Site-wide trackers (Meta PageView, Reddit PageVisit, the Google tag,
// Bing, LinkedIn, Clarity) come from `__root.tsx`, which every document
// shares; only the purchase-stage trackers are declared here.
//
// The loader calls `Billing.Confirm` during SSR, and `head()` bakes the
// resulting pixel values into the inline scripts, so they fire when the
// head parses — no useEffect race. Every unknown resolves to no pixel
// (ghost URL, test bypass, decline-redirect, backend error): a
// fabricated conversion value is worse than a missing one.
//
// @see docs/projects/active/audit-cookie-trackers.md
export const Route = createFileRoute('/post-purchase')({
  validateSearch: PostPurchaseSearchSchema,
  loaderDeps: ({ search }) => purchaseReturnDeps(search),
  loader: ({ deps, abortController }) =>
    confirmPurchaseReturn(deps, abortController.signal),
  head: ({ loaderData }) => ({
    meta: [
      ...seo({
        title: m.meta_post_purchase_title(),
        description: m.meta_post_purchase_description(),
      }),
    ],
    scripts: purchaseReturnScripts(loaderData),
  }),
  component: Page,
});

function Page() {
  useCheckoutReturnCleanup();
  usePurchaseCompletedEvent(Route.useLoaderData());

  return (
    <>
      <h1 className='sr-only'>{m.meta_post_purchase_title()}</h1>

      <PostPurchaseNavHeader />
      <main className='flex-1'>
        <PostPurchaseCarouselContent />
      </main>
      <Footer />
    </>
  );
}
